/**
 * Partner Program entity class
 */

/**
 * PartnerProductFactory
 * returns Partner Program entity class
 *
 * @class PartnerProductFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.PartnerProduct} class
 *
 * @ngdoc factory
 * @name mwApp.factory:PartnerProductFactory
 */
import { has } from 'app/helpers/object'
import { PARTNER_PRODUCT_LOCALIZATION_PAIRS, metadataMapToLocalizations, localizationsToMetadataMap } from '../../../app/helpers/localization'

/* @ngInject */
export default function PartnerProductFactory ($http, $injector, alertLiteSvc, Entity, FileUploadMixin) {
  /* === PartnerProduct class === */

  /**
   * PartnerProduct model
   *
   * @class PartnerProduct
   * @classdesc PartnerProduct model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.m2mMixin
   * @mixes models.onOffMixin
   * @mixes models.upDownMixin
   * @return {models.PartnerProduct} PartnerProduct entity
   */
  function PartnerProduct () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  PartnerProduct.prototype = Object.create(Entity.prototype)
  PartnerProduct.prototype.constructor = PartnerProduct

  /* === PartnerProduct instance methods & fields === */

  Object.assign(PartnerProduct.prototype, {

    /**
     * instance fields
     *
     * @memberof models.PartnerProduct#
     * @type {Object}
     */
    _fields: {
      id: Number,
      coverImageUrl: String,
      verticalImageUrl: String,
      description: String,
      richDescription: String,
      externalId: String,
      externalProductId: String,
      length: 30,
      lengthType: 'Days',
      monetization: {
        id: Number,
        subscriptionId: Number,
        periodLength: Number,
        periodTimeUnit: String,
        amount: Number,
        type: String
      },
      name: String,
      networkId: Number,
      partnerProgramCoverImageUrl: String,
      partnerProgramName: String,
      partnerProgramId: Number,
      productId: String,
      updatedBy: String,
      updatedTime: String,
      localizationMetadata: {}, // map<langCode, {name, description, coverImageUrl, verticalImageUrl}>
      $partnerProgram: {}
    },

    /**
     * Prepare partner product
     *
     * @memberof models.PartnerProduct#
     * @override
     */
    _prepare: function () {
      this.localizations = metadataMapToLocalizations(this.localizationMetadata, PARTNER_PRODUCT_LOCALIZATION_PAIRS)
    },

    /**
     * url template
     *
     * @memberof models.PartnerProduct#
     * @type {String}
     */
    _urlTemplate: 'api/networks/{{::networkId}}/partner-product/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.PartnerProduct#
     * @type {String}
     */
    _publicUrlTemplate: 'organizations/{{::networkId}}/partner-product/{{::id}}',

    /**
     * url template for loading custom prices
     *
     * @memberof models.PartnerProduct#
     * @type {String}
     */
    _customPricesUrlTemplate: 'api/networks/{{::networkId}}/subscriptions/{{::subscriptionId}}/partner-product/{{::id}}/prices',

    /**
     * Get underlying object
     *
     * @memberof models.PartnerProduct#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      this.partnerProgramId = this.$partnerProgram?.id
      this.localizationMetadata = localizationsToMetadataMap(this.localizations, PARTNER_PRODUCT_LOCALIZATION_PAIRS)
      return Entity.prototype._object.apply(this, arguments)
    }
  }, FileUploadMixin)

  /* === PartnerProduct static methods & fields === */

  PartnerProduct.loadPartnerProducts = function (ids, tableState) {
    const cast = $injector.get(this._name) // get constructor from injector by name
    let url = `${cast.prototype.$_urlTemplate(ids)}`
    url = url[url.length - 1] === '/' ? url.substring(0, url.length - 1) : url

    const params = {
      page: tableState.page || 0,
      count: tableState.count,
      name: tableState.name,
      q: tableState.q
    }
    return $http.get(url, { params })
      .then(
        (response) => {
          const data = has('payload')(response.data)
            ? response.data.payload
            : response.data

          for (const item of data.content) {
            item.networkId = ids.networkId
          }

          const result = this.cast(data, {
            // response structure
            content: [cast._name],
            number: 0,
            size: 0,
            first: true,
            last: true
          })

          tableState.page = result.number
          tableState.count = result.size // page size

          return result
        }
      ).catch(alertLiteSvc.alertError)
  }

  // copy all static methods from Entity class
  Object.assign(PartnerProduct, Entity)
  PartnerProduct._name = 'PartnerProduct'
  PartnerProduct.init()

  // permissions
  PartnerProduct.access = {
  }

  // extra
  PartnerProduct.extra = {
  }
  return PartnerProduct
}
